// Словарь ИИ: бриф сделки, черновик сметы, клиентские тексты, настройки и журнал. Подключается в `ru.ts`
// одной строкой, чтобы не мешать соседним разделам.

/** Тексты ошибок `ai.*` (раздел 12 спецификации подпроекта 3). */
export const ruAiErrors = {
  not_configured: 'ИИ не подключён: на сервере не задан ключ BotHub. Продолжайте вручную',
  unavailable: 'ИИ сейчас недоступен — продолжите вручную',
  invalid_answer: 'ИИ ответил непонятно — попробуйте ещё раз или продолжите вручную',
  limit_reached:
    'Лимит трат на ИИ в этом месяце исчерпан — продолжите вручную или попросите админа поднять лимит',
  no_specializations:
    'В справочнике нет специализаций — добавьте их в «Команде», тогда ИИ сможет оценить часы',
  estimate_empty: 'В смете нет работ — тексты писать не о чем',
  run_finished: 'Этот запуск уже закончен — отменять нечего',
  nothing_to_undo: 'Отменять нечего: результат уже отменён или не записывался',
  service_description_required: 'Опишите услугу — до 4 000 знаков: что входит и что получает клиент',
  no_categories: 'В каталоге нет категорий — добавьте хотя бы одну, тогда ИИ сможет составить услугу',
  site_catalog_empty: 'В каталоге нет услуг — ИИ не из чего подобрать. Добавьте услуги или «Быстрый расчёт»',
  settings_model_invalid: 'Название модели — без пробелов, до 100 знаков; выберите её из списка BotHub',
  settings_price_invalid: 'Цена за миллион токенов — от 0 до 1 000 000 ₽',
  settings_limit_invalid: 'Лимит трат — от 0 до 10 000 000 ₽ в месяц',
} as const;

/** Ошибки брифа сделки (раздел `errors.estimates`). */
export const ruBriefErrors = {
  brief_text_too_long: 'Поле брифа длиннее 4 000 знаков — сократите',
  brief_short_too_long: 'Бюджет и сроки в брифе — не длиннее 300 знаков',
  brief_questions_too_many: 'Вопросов клиенту в брифе — не больше 20',
  brief_question_too_long: 'Вопрос клиенту длиннее 300 знаков — сократите',
  brief_options_too_many: 'Вариантов ответа у вопроса — не больше 4',
  brief_option_too_long: 'Вариант ответа длиннее 120 знаков',
  brief_answer_too_long: 'Ответ длиннее 1000 знаков',
  brief_empty: 'Заполните в брифе хотя бы цель или то, что нужно сделать',
  brief_nothing_to_restore: 'Возвращать нечего: ИИ бриф не перезаписывал',
  brief_not_approved: 'Сначала утвердите бриф',
  estimate_required: 'Сначала заведите смету',
} as const;

export const ruAiAudit = {
  actions: {
    estimate_brief: {
      created: 'Создан бриф',
      changed: 'Изменён бриф',
      approved: 'Бриф утверждён',
      reopened: 'Бриф возвращён в черновик',
      generated: 'Бриф собран ИИ',
      restored: 'Бриф возвращён к прежнему',
    },
    ai_settings: {
      changed: 'Изменены настройки ИИ',
    },
  },
  entities: {
    estimate_brief: 'Бриф сделки',
    ai_settings: 'Настройки ИИ',
  },
  fields: {
    briefGoal: 'Цель',
    briefAudience: 'Аудитория',
    briefNeeds: 'Что нужно',
    briefWishes: 'Пожелания',
    briefConstraints: 'Ограничения',
    briefBudget: 'Бюджет по брифу',
    briefDeadline: 'Сроки по брифу',
    briefQuestions: 'Что уточнить у клиента',
    aiBriefModel: 'Модель для брифа',
    aiEstimateModel: 'Модель для сметы',
    aiTextsModel: 'Модель для текстов',
    aiTzModel: 'Модель для ТЗ проекта',
    aiTasksModel: 'Модель для задач проекта',
    aiServiceModel: 'Модель для услуг каталога',
    aiSiteModel: 'Модель для конфигуратора сайта',
    aiPrices: 'Цены моделей',
    aiMonthlyLimit: 'Лимит трат в месяц',
    aiStages: 'Этапов от ИИ',
    aiTasks: 'Работ от ИИ',
  },
} as const;

export const ruAiNotifications = {
  typeAiReady: 'Черновик ИИ готов',
  typeAiReadyHint: 'ИИ закончил бриф, черновик сметы или клиентские тексты, которые вы запустили',
  typeAiFailed: 'ИИ не справился',
  typeAiFailedHint: 'Запуск ИИ закончился ошибкой — продолжите вручную или повторите',
  typeAiLimit: 'Лимит трат на ИИ',
  typeAiLimitHint: 'Траты на ИИ в этом месяце подошли к лимиту или дошли до него',
} as const;

export const ruAi = {
  tasks: {
    brief: 'Бриф',
    estimate: 'Черновик сметы',
    texts: 'Тексты КП',
    tz: 'ТЗ проекта',
    tasks: 'Задачи проекта',
    service: 'Услуга каталога',
    site: 'Конфигуратор сайта',
  },
  phases: {
    queued: 'В очереди',
    collecting: 'Собираю материалы сделки',
    thinking: 'ИИ думает — это до двух минут',
    checking: 'Проверяю ответ',
    saving: 'Записываю',
  },
  run: {
    cancel: 'Отменить',
    cancelled: 'Запуск отменён — ничего не записано',
    failed: 'ИИ не справился',
    manual: 'Продолжайте вручную: всё работает и без ИИ',
    retry: 'Повторить',
    undo: 'Отменить результат',
    undone: 'Результат отменён',
    undoFailed: 'Не удалось отменить результат',
    startFailed: 'Не удалось запустить ИИ',
    cancelFailed: 'Не удалось отменить запуск',
    by: 'Запустил {name}',
    model: 'Модель: {model}',
    progress: 'Ход работы ИИ',
  },
  status: {
    off: 'ИИ не подключён',
    offHint: 'Админу нужно задать ключ BotHub на сервере. Пока всё делается вручную',
    limit: 'Лимит трат на ИИ исчерпан',
    limitHint: 'Попросите админа поднять лимит в «Настройки → ИИ» или продолжайте вручную',
    readOnly: 'Запускать ИИ может ответственный за сделку или менеджер со всеми продажами',
    closed: 'Сделка закрыта — ИИ не запускается',
  },
  hints: {
    privacy: {
      title: 'Что уходит в ИИ',
      text: 'Поля сделки, карточка клиента, сводки касаний, сообщения чата сделки, письма клиента и текст приложенных файлов (PDF, DOCX, TXT, MD). Телефоны, почты, номера счетов и карт перед отправкой заменяются заглушками. Текст запросов нигде не хранится — в журнале только модель, токены и стоимость',
      example: 'Телефон клиента в переписке уходит в ИИ как «[телефон]», и сам ИИ его не видит',
    },
    approve: {
      title: 'Зачем утверждать бриф',
      text: 'Смету ИИ строит только по утверждённому брифу. Если поправить утверждённый бриф, он вернётся в черновик — смета строилась по другому тексту, и его нужно утвердить заново',
      example: 'Клиент поднял бюджет, вы поправили поле «Бюджет» — снова «Утвердить бриф», и ИИ учтёт новое',
    },
    draft: {
      title: 'Как ИИ строит смету',
      text: 'По утверждённому брифу, каталогу услуг и прошлым отправленным сметам студии ИИ предлагает этапы, работы, часы и отметки пакетов. Всё от ИИ — черновик: часы и состав проверяете вы, а перед КП ещё и админ',
      example:
        'ИИ взял из каталога «Дизайн лендинга» и поставил 24 часа; вы поправили до 30 — остальное осталось',
    },
    limit: {
      title: 'Лимит трат',
      text: 'Когда траты за месяц дойдут до лимита, кнопки ИИ отключатся до начала следующего месяца, а админы получат уведомление. Ручной путь работает как всегда. При 80 % лимита уведомление приходит заранее',
      example: 'Лимит 3 000 ₽: на 2 400 ₽ придёт «подходит к лимиту», на 3 000 ₽ — «исчерпан»',
    },
    prices: {
      title: 'Цена за миллион токенов',
      text: 'BotHub присылает в ответе только токены, а деньги считает платформа по этим ценам — они нужны для журнала и лимита. Возьмите их из тарифа BotHub для выбранной модели',
      example: 'Модель стоит 300 ₽ за 1 млн токенов запроса: бриф на 20 000 токенов обойдётся примерно в 6 ₽',
    },
  },
  brief: {
    title: 'Бриф сделки',
    blockTitle: 'Бриф',
    blockNone: 'Брифа ещё нет — соберите его с ИИ из материалов сделки или заполните вручную',
    blockNoneReadOnly: 'Брифа ещё нет',
    collect: 'Собрать бриф',
    recollect: 'Собрать заново',
    open: 'Открыть бриф',
    approve: 'Утвердить бриф',
    reopen: 'Вернуть в черновик',
    statuses: { draft: 'Черновик', approved: 'Утверждён' },
    approvedBy: 'Утвердил {name}, {date}',
    generatedAt: 'Собран ИИ: {date}',
    overwrite: 'ИИ перезапишет поля брифа. Прежний вариант можно вернуть кнопкой «Отменить»',
    emptyTitle: 'Брифа ещё нет',
    emptyText:
      'ИИ прочитает сделку, переписку и письма клиента и предложит черновик брифа — вы поправите и утвердите. Или заполните поля ниже сами',
    approveNeedsText: 'Заполните хотя бы цель или то, что нужно сделать',
    saveFailed: 'Не удалось сохранить бриф',
    approveFailed: 'Не удалось утвердить бриф',
    fields: {
      goal: 'Цель проекта',
      audience: 'Аудитория',
      needs: 'Что нужно сделать',
      wishes: 'Пожелания',
      constraints: 'Ограничения',
      budget: 'Бюджет',
      deadline: 'Сроки',
    },
    placeholders: {
      goal: 'Чего клиент хочет добиться',
      audience: 'Кто будет пользоваться результатом',
      needs: 'Разделы, функции, интеграции',
      wishes: 'Что клиенту нравится, примеры, стиль',
      constraints: 'Технологии, запреты, юридические требования',
      budget: 'Например, до 300 000 ₽',
      deadline: 'Например, к 1 декабря',
    },
    questions: {
      title: 'Что уточнить',
      lead: 'Чего ИИ не нашёл в материалах. Выберите вариант, напишите свой ответ или отметьте «Спросить клиента» — ответы пойдут в смету',
      empty: 'Вопросов нет — ИИ добавит их, если чего-то не хватит',
      add: 'Добавить вопрос',
      remove: 'Удалить вопрос',
      removed: 'Вопрос удалён',
      placeholder: 'Вопрос',
      text: 'Вопрос № {number}',
      answered: 'Отвечено {done} из {total}',
      copy: 'Скопировать клиенту',
      copyHint: 'Нумерованный список: вопросы без ответа и отмеченные «Спросить клиента»',
      copyNothing: 'Все вопросы отвечены — копировать нечего',
      copied: 'Вопросы скопированы',
      copyFailed: 'Не удалось скопировать',
      options: 'Варианты ответа на вопрос № {number}',
      own: 'Свой ответ',
      ownLabel: 'Свой ответ на вопрос № {number}',
      askClient: 'Спросить клиента',
      edit: 'Изменить',
      unanswered: 'Без ответа: {count}',
    },
    sources: {
      title: 'Что учтено',
      empty: 'Появится, когда ИИ соберёт бриф',
      deal: 'Поля сделки и карточка клиента',
      touches: 'Касаний: {count}',
      messages: 'Сообщений чата сделки: {count}',
      mails: 'Писем клиента: {count}',
      files: 'Файлы',
      fileUsed: 'прочитан',
      fileSkipped: 'не прочитан — формат или размер',
      truncated: 'Материалов много — самое старое отброшено, чтобы уложиться в объём',
      masked:
        'Скрыто перед отправкой в ИИ: телефонов — {phones}, почт — {emails}, счетов — {accounts}, карт — {cards}',
      maskedNone: 'Контактных и платёжных данных в материалах не нашлось',
    },
    history: 'История брифа',
    result: 'Бриф собран: вопросов клиенту — {questions}. Это черновик — проверьте и утвердите',
  },
  draft: {
    button: 'Черновик от ИИ',
    title: 'Черновик сметы от ИИ',
    lead: 'ИИ предложит этапы, работы, часы и отметки пакетов по утверждённому брифу, каталогу и прошлым сметам студии. Это черновик — проверьте часы',
    needBrief: 'Сначала утвердите бриф',
    toBrief: 'Открыть бриф',
    start: 'Составить',
    modes: {
      append: 'Добавить к смете',
      appendText: 'Новые этапы встанут в конец, ваши останутся как есть',
      replace_ai: 'Заменить прежнее от ИИ',
      replace_aiText: 'Этапы, которые ИИ уже написал и вы не трогали, будут заменены новыми',
    },
    result: 'Черновик записан: этапов — {stages}, работ — {tasks}, часов — {hours}',
    assumptions: 'Допущения ИИ',
    questions: 'Что уточнить у клиента',
    stageRegenerate: 'Пересобрать этап с ИИ',
    stageResult: 'Этап пересобран: работ — {tasks}, часов — {hours}',
    details: 'Допущения и вопросы',
    emptyPackages: 'Пустые пакеты ИИ не заполнял — отметки «Б / О / М» проверьте сами',
  },
  settings: {
    title: 'ИИ',
    connection: {
      title: 'Подключение',
      lead: 'ИИ работает через BotHub — это единственная внешняя служба платформы',
      on: 'Ключ BotHub задан — ИИ работает',
      off: 'Ключ BotHub не задан: кнопки ИИ неактивны, всё делается вручную',
      how: 'Ключ задаёт админ сервера переменной BOTHUB_API_KEY у api и worker. Получить его: bothub.chat → «Для разработчиков» → «Добавить ключ»',
    },
    models: {
      title: 'Модели',
      lead: 'Своя модель на каждую задачу. Цены нужны, чтобы считать траты, — возьмите их из тарифа BotHub',
      model: 'Модель',
      modelPlaceholder: 'Например, claude-sonnet-5.5',
      inputPrice: 'Запрос, за 1 млн токенов',
      outputPrice: 'Ответ, за 1 млн токенов',
      pick: 'Выбрать из списка BotHub',
      pickTitle: 'Модели BotHub',
      pickSearch: 'Найти модель',
      pickEmpty: 'Ничего не найдено',
      pickFailed: 'Список моделей не получен — введите название вручную',
      pickOff: 'Без ключа список недоступен',
      saveFailed: 'Не удалось сохранить настройки ИИ',
    },
    limit: {
      title: 'Лимит трат',
      lead: 'Сколько в месяц можно потратить на ИИ',
      label: 'Лимит в месяц',
      none: 'Без лимита',
      noneText: 'ИИ тратит сколько нужно — следите за журналом',
      on: 'Когда траты дойдут до лимита, ИИ отключится до следующего месяца',
    },
    usage: {
      title: 'Траты за {month}',
      spent: 'Потрачено',
      of: 'из {limit}',
      noLimit: 'без лимита',
      calls: 'Вызовов: {count}',
      tokens: 'Токены: {in} запроса, {out} ответа',
      byTask: 'По задачам',
      states: {
        none: 'Лимит не задан',
        ok: 'В пределах лимита',
        warning: 'Подходит к лимиту',
        reached: 'Лимит исчерпан',
      },
    },
    calls: {
      title: 'Журнал вызовов',
      lead: 'Модель, токены и стоимость — без текста запросов и ответов',
      empty: 'Вызовов ещё не было',
      emptyText: 'Здесь появятся бриф, черновики смет и тексты, которые запускал ИИ',
      more: 'Показать ещё',
      failed: 'Журнал не загрузился',
      columns: {
        at: 'Когда',
        task: 'Задача',
        model: 'Модель',
        tokens: 'Токены',
        cost: 'Стоимость',
        actor: 'Кто',
        deal: 'Сделка',
        status: 'Итог',
      },
      tokensIn: 'вход',
      tokensOut: 'выход',
      statuses: { ok: 'Принят', invalid: 'Ответ неверный', error: 'Ошибка службы', cancelled: 'Отменён' },
      seconds: '{value} с',
    },
    history: 'История настроек',
  },
  palette: {
    settings: 'Настройки ИИ',
    collectBrief: 'Собрать бриф с ИИ',
    openBrief: 'Открыть бриф',
    draftEstimate: 'Составить смету с ИИ',
  },
  notifications: {
    ready: {
      brief: 'Бриф собран: {deal}',
      estimate: 'Черновик сметы готов: {deal}',
      texts: 'Тексты КП готовы: {deal}',
      tz: 'Черновик ТЗ готов: {deal}',
      tasks: 'Черновик задач готов: {deal}',
      service: 'Черновик услуги готов',
      site: 'ИИ подобрал услуги для конфигуратора сайта',
    },
    readyBody: 'Это черновик — проверьте и поправьте',
    failed: {
      brief: 'ИИ не собрал бриф: {deal}',
      estimate: 'ИИ не составил смету: {deal}',
      texts: 'ИИ не написал тексты КП: {deal}',
      tz: 'ИИ не составил ТЗ: {deal}',
      tasks: 'ИИ не составил задачи: {deal}',
      service: 'ИИ не составил услугу',
      site: 'ИИ не подобрал услуги для конфигуратора сайта',
    },
    limitWarning: 'Траты на ИИ подходят к лимиту месяца',
    limitReached: 'Лимит трат на ИИ в этом месяце исчерпан',
    limitBody: 'Потрачено {spent} из {limit}. ИИ-кнопки в этом месяце не работают, ручной путь — как обычно',
  },
} as const;
